import { useMemo, useState } from 'react'
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import Chart from 'react-apexcharts'
import { useGetDashboardStats } from '../../../APIS'
import AddPeopleCountModal from './_components/AddPeopleCountModal'
import DownloadReportButton from './_components/DownloadReportButton'
import { Can } from '../../../context/Can'
import { getCurrentUser } from '../../components/PluginsMenu/Nestable/utils'

const toNumber = (value) => {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

const formatCurrency = (value) => `PKR ${toNumber(value).toLocaleString()}`

const formatDateInput = (date) => {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getCurrentMonthRange = () => {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), 1)
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)

  return {
    startDate: formatDateInput(start),
    endDate: formatDateInput(end),
  }
}

const normalizeRows = (stats) => {
  if (!Array.isArray(stats)) return []

  const rows = []

  stats.forEach((service) => {
    const serviceName = service?.name || 'Unknown'
    const pricingArray = service?.pricing || []

    if (Array.isArray(pricingArray)) {
      pricingArray.forEach((item, index) => {
        const price = toNumber(item?.price ?? 0)
        const quantity = toNumber(item?.total_tickets ?? 0)
        const total = toNumber(item?.total_amount ?? price * quantity)
        const refund = toNumber(item?.total_refund_amount ?? 0)
        const freeTierTickets = price === 0 ? quantity : 0

        rows.push({
          id: `${service?.id}-${index}`,
          name: serviceName,
          description: item?.desc || '-',
          price,
          quantity,
          total,
          refund,
          freeTierTickets,
          baseValidation: toNumber(item?.base_validation ?? 0),
          midUpValidation: toNumber(item?.mid_up_validation ?? 0),
          topValidation: toNumber(item?.top_validation ?? 0),
          midDownValidation: toNumber(item?.mid_down_validation ?? 0),
          netRevenue: total - refund,
        })
      })
    }
  })

  return rows
}

const getOverviewStats = (rows) => {
  return {
    totalReservations: rows.reduce((acc, item) => acc + item.quantity, 0),
    totalRevenue: rows.reduce((acc, item) => acc + item.total, 0),
    totalRefund: rows.reduce((acc, item) => acc + item.refund, 0),
    freeTierTickets: rows.reduce((acc, item) => acc + item.freeTierTickets, 0),
    netRevenue: rows.reduce((acc, item) => acc + item.netRevenue, 0),
  }
}

const groupRowsByName = (rows) => {
  return rows.reduce((acc, row) => {
    if (!acc[row.name]) {
      acc[row.name] = []
    }
    acc[row.name].push(row)
    return acc
  }, {})
}

const transformServiceName = (serviceId, service) => {
  const serviceNameMap = {
    1: 'Chairlift & Cable Car',
    2: 'Sightseeing',
    3: 'Boating',
    4: 'Parking',
    5: 'Golf Cart',
    6: 'Softwheel Train',
  }

  if (serviceId && serviceNameMap[Number(serviceId)]) {
    return serviceNameMap[Number(serviceId)]
  }

  if (!service) return 'All Pricings'
  return service
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const getChartData = (rows) => {
  const descriptionMap = {}

  rows.forEach((row) => {
    if (!descriptionMap[row.description]) {
      descriptionMap[row.description] = 0
    }
    descriptionMap[row.description] += row.quantity
  })

  const descriptions = Object.keys(descriptionMap)
  const quantities = Object.values(descriptionMap)

  return {
    series: [
      {
        name: 'Total Reservations',
        data: quantities,
      },
    ],
    options: {
      chart: {
        type: 'bar',
        height: 350,
        toolbar: {
          show: true,
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 4,
          dataLabels: {
            position: 'top',
          },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -20,
        style: {
          fontSize: '12px',
          colors: ['#304758'],
        },
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      xaxis: {
        categories: descriptions,
        title: {
          text: 'Pricing Tiers',
        },
      },
      yaxis: {
        title: {
          text: 'Total Reservations (Tickets)',
        },
      },
      fill: {
        opacity: 1,
        colors: ['#4cbc9a'],
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val.toLocaleString() + ' tickets'
          },
        },
      },
    },
  }
}

const NameSection = ({ name, rows, showValidationColumns }) => {
  const stats = {
    totalReservations: rows.reduce((acc, item) => acc + item.quantity, 0),
    totalRevenue: rows.reduce((acc, item) => acc + item.total, 0),
    totalRefund: rows.reduce((acc, item) => acc + item.refund, 0),
    freeTierTickets: rows.reduce((acc, item) => acc + item.freeTierTickets, 0),
    netRevenue: rows.reduce((acc, item) => acc + item.netRevenue, 0),
  }

  return (
    <Card className='mb-4'>
      <Card.Header className='d-flex justify-content-between align-items-center'>
        <Card.Title className='mb-0'>{name}</Card.Title>
        <small className='text-muted'>{rows.length} pricing entries</small>
      </Card.Header>
      <Card.Body>
        <Row className='g-3 mb-3'>
          <Col xl={3} md={6}>
            <Card className='border h-100'>
              <Card.Body>
                <div className='text-muted small'>Total Reservations</div>
                <div className='text-muted small mb-2'>({stats.totalReservations.toLocaleString()} Tickets)</div>
                <h5 className='mb-0'>{formatCurrency(stats.totalRevenue)}</h5>
              </Card.Body>
            </Card>
          </Col>
          <Col xl={3} md={6}>
            <Card className='border h-100'>
              <Card.Body>
                <div className='text-muted small'>Total Refund</div>
                <div className='text-muted small mb-2'>(Refund)</div>
                <h5 className='mb-0'>{formatCurrency(stats.totalRefund)}</h5>
              </Card.Body>
            </Card>
          </Col>
          <Col xl={3} md={6}>
            <Card className='border h-100'>
              <Card.Body>
                <div className='text-muted small'>Free Tier Tickets</div>
                <div className='text-muted small mb-2'>({stats.freeTierTickets.toLocaleString()} Tickets)</div>
                <h5 className='mb-0'>{formatCurrency(0)}</h5>
              </Card.Body>
            </Card>
          </Col>
          <Col xl={3} md={6}>
            <Card className='border h-100'>
              <Card.Body>
                <div className='text-muted small'>Net Revenue</div>
                <div className='text-muted small mb-2'>(Revenue)</div>
                <h5 className='mb-0'>{formatCurrency(stats.netRevenue)}</h5>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <div className='table-responsive'>
          <table className='table table-striped mb-0'>
            <thead>
              <tr>
                <th>Ticket Category</th>
                <th>Price</th>
                <th>Quantity</th>
                {showValidationColumns && <th>Base</th>}
                {showValidationColumns && <th>Mid Up</th>}
                {showValidationColumns && <th>Top</th>}
                {showValidationColumns && <th>Mid Down</th>}
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.description}</td>
                  <td>{formatCurrency(row.price)}</td>
                  <td>{row.quantity.toLocaleString()}</td>
                  {showValidationColumns && <td>{row.baseValidation.toLocaleString()}</td>}
                  {showValidationColumns && <td>{row.midUpValidation.toLocaleString()}</td>}
                  {showValidationColumns && <td>{row.topValidation.toLocaleString()}</td>}
                  {showValidationColumns && <td>{row.midDownValidation.toLocaleString()}</td>}
                  <td>{formatCurrency(row.netRevenue)}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 500 }}>
                <td>Sub Total</td>
                <td>{formatCurrency(stats.totalRevenue)}</td>
                <td>{stats.totalReservations.toLocaleString()}</td>
                {showValidationColumns && <td></td>}
                {showValidationColumns && <td></td>}
                {showValidationColumns && <td></td>}
                {showValidationColumns && <td></td>}
                <td>{formatCurrency(stats.totalRevenue)}</td>
              </tr>
              <tr style={{ fontWeight: 500 }}>
                <td>Refunded</td>
                <td>{formatCurrency(stats.totalRefund)}</td>
                <td>{stats.totalRefund.toLocaleString()}</td>
                {showValidationColumns && <td></td>}
                {showValidationColumns && <td></td>}
                {showValidationColumns && <td></td>}
                {showValidationColumns && <td></td>}
                <td>{formatCurrency(stats.totalRefund)}</td>
              </tr>
              <tr style={{ fontWeight: 500 }}>
                <td>Total</td>
                <td>{formatCurrency(stats.netRevenue)}</td>
                <td>{stats.totalReservations.toLocaleString()}</td>
                {showValidationColumns && <td></td>}
                {showValidationColumns && <td></td>}
                {showValidationColumns && <td></td>}
                {showValidationColumns && <td></td>}
                <td>{formatCurrency(stats.netRevenue)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {rows.length > 0 && (
          <div className='mt-4'>
            <Chart
              options={getChartData(rows).options}
              series={getChartData(rows).series}
              type='bar'
              height={350}
            />
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

const Dashboard = () => {
  const { serviceId, service } = useParams()
  const showValidationColumns = Number(serviceId) === 1
  const monthRange = useMemo(() => getCurrentMonthRange(), [])
  const [startDate, setStartDate] = useState(monthRange.startDate)
  const [endDate, setEndDate] = useState(monthRange.endDate)
  const [showAddPeopleCountModal, setShowAddPeopleCountModal] = useState(false)

  const { city_id } = getCurrentUser() || {}

  const { data: stats, isLoading, isError, error } = useGetDashboardStats(serviceId, startDate, endDate, city_id)

  const rows = useMemo(() => normalizeRows(stats), [stats])
  const topStats = useMemo(() => getOverviewStats(rows), [rows])
  const groupedRows = useMemo(() => groupRowsByName(rows), [rows])

  if (isLoading) {
    return (
      <div className='container-fluid'>
        <Card>
          <Card.Body>
            <div className='d-flex justify-content-center py-5'>
              <Spinner animation='border' />
            </div>
          </Card.Body>
        </Card>
      </div>
    )
  }

  if (isError) {
    return (
      <div className='container-fluid'>
        <Card>
          <Card.Body>
            <Alert variant='danger' className='mb-0'>
              Failed to load dashboard stats. {error?.response?.data?.message || 'Please try again.'}
            </Alert>
          </Card.Body>
        </Card>
      </div>
    )
  }

  return (
    <div className='container-fluid'>
      <Card className='mb-4'>
        <Card.Header>
          <Card.Title className='mb-0'>Date Range Filter</Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className='g-3 align-items-end'>
            <Col md={2}>
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type='date'
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type='date'
                value={endDate}
                min={startDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </Col>
            <Can I="view" a="Add People Count">
              <Col md={2}>
                <Button onClick={() => setShowAddPeopleCountModal(true)} className='w-100'>
                  Add Count
                </Button>
              </Col>
            </Can>
            <Col md={4}>
              <DownloadReportButton contentSelector='#dashboard-report-content' filename='dashboard-report' />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div id='dashboard-report-content'>
        <Row className='g-3 mb-4'>
          <Col xl={3} md={6}>
            <Card className='h-100'>
              <Card.Body>
                <div className='text-muted small'>Total Reservations</div>
                <div className='text-muted small mb-2'>({topStats.totalReservations.toLocaleString()} Tickets)</div>
                <h4 className='mb-0'>{formatCurrency(topStats.totalRevenue)}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col xl={3} md={6}>
            <Card className='h-100'>
              <Card.Body>
                <div className='text-muted small'>Total Refund</div>
                <div className='text-muted small mb-2'>({topStats.totalRefund > 0 ? 18 : 0} Tickets)</div>
                <h4 className='mb-0'>{formatCurrency(topStats.totalRefund)}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col xl={3} md={6}>
            <Card className='h-100'>
              <Card.Body>
                <div className='text-muted small'>Free Tier Tickets</div>
                <div className='text-muted small mb-2'>({topStats.freeTierTickets.toLocaleString()} Tickets)</div>
                <h4 className='mb-0'>{formatCurrency(0)}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col xl={3} md={6}>
            <Card className='h-100'>
              <Card.Body>
                <div className='text-muted small'>Net Revenue</div>
                <div className='text-muted small mb-2'>(Revenue)</div>
                <h4 className='mb-0'>{formatCurrency(topStats.netRevenue)}</h4>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className='mb-4'>
          <Card.Header>
            <Card.Title className='mb-0'>{transformServiceName(serviceId, service)}</Card.Title>
          </Card.Header>
          <Card.Body>
            {rows.length ? (
              <div className='table-responsive'>
                <table className='table table-striped mb-0'>
                  <thead>
                    <tr>
                      <th>Ticket Category</th>
                      <th>Price</th>
                      <th>Quantity</th>
                      {showValidationColumns && <th>Base</th>}
                      {showValidationColumns && <th>Mid Up</th>}
                      {showValidationColumns && <th>Top</th>}
                      {showValidationColumns && <th>Mid Down</th>}
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={row.id}>
                        <td>{row.description}</td>
                        <td>{formatCurrency(row.price)}</td>
                        <td>{row.quantity.toLocaleString()}</td>
                        {showValidationColumns && <td>{row.baseValidation.toLocaleString()}</td>}
                        {showValidationColumns && <td>{row.midUpValidation.toLocaleString()}</td>}
                        {showValidationColumns && <td>{row.topValidation.toLocaleString()}</td>}
                        {showValidationColumns && <td>{row.midDownValidation.toLocaleString()}</td>}
                        <td>{formatCurrency(row.netRevenue)}</td>
                      </tr>
                    ))}
                    <tr style={{ fontWeight: 500 }}>
                      <td>Sub Total</td>
                      <td>{formatCurrency(topStats.totalRevenue)}</td>
                      <td>{topStats.totalReservations.toLocaleString()}</td>
                      {showValidationColumns && <td></td>}
                      {showValidationColumns && <td></td>}
                      {showValidationColumns && <td></td>}
                      {showValidationColumns && <td></td>}
                      <td>{formatCurrency(topStats.totalRevenue)}</td>
                    </tr>
                    <tr style={{ fontWeight: 500 }}>
                      <td>Refunded</td>
                      <td>{formatCurrency(topStats.totalRefund)}</td>
                      <td>{topStats.totalRefund.toLocaleString()}</td>
                      {showValidationColumns && <td></td>}
                      {showValidationColumns && <td></td>}
                      {showValidationColumns && <td></td>}
                      {showValidationColumns && <td></td>}
                      <td>{formatCurrency(topStats.totalRefund)}</td>
                    </tr>
                    <tr style={{ fontWeight: 500 }}>
                      <td>Total</td>
                      <td>{formatCurrency(topStats.netRevenue)}</td>
                      <td>{topStats.totalReservations.toLocaleString()}</td>
                      {showValidationColumns && <td></td>}
                      {showValidationColumns && <td></td>}
                      {showValidationColumns && <td></td>}
                      {showValidationColumns && <td></td>}
                      <td>{formatCurrency(topStats.netRevenue)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <p className='text-muted mb-0'>No pricing data found for selected range.</p>
            )}
          </Card.Body>
        </Card>

        {rows.length > 0 && (
          <Card className='mb-4'>
            <Card.Body>
              <Chart
                options={getChartData(rows).options}
                series={getChartData(rows).series}
                type='bar'
                height={350}
              />
            </Card.Body>
          </Card>
        )}

        {Object.entries(groupedRows).map(([name, sectionRows]) => (
          <NameSection key={name} name={name} rows={sectionRows} showValidationColumns={showValidationColumns} />
        ))}
      </div>

      <AddPeopleCountModal show={showAddPeopleCountModal} onHide={() => setShowAddPeopleCountModal(false)} />
    </div>
  )
}

export default Dashboard
